import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import apiClient from '../../helpers/apiClient';
import { objectForReq } from '../../helpers/articleHelpers';
import { authHeader } from '../../helpers/authHelpers';
import { logger } from '../../helpers/logger';

export interface ArticleImage {
  url?: string;
  altText?: string;
}

export interface ArticleReadTime {
  icon?: string;
  label?: string;
}

export interface ArticleArchivedState {
  isArchived?: boolean;
  label?: string;
}

export interface ArticleListItem {
  id?: string;
  title?: string;
  url?: string;
  urlText?: string;
  introduction?: string;
  publishedDate?: string;
  category?: unknown[];
  readTime?: ArticleReadTime;
  image?: ArticleImage;
  archived?: ArticleArchivedState;
  icon?: string;
  [key: string]: unknown;
}

export interface ArticlesFilters {
  availableFilters?: Record<string, unknown>;
  appliedFilters?: Record<string, unknown>;
}

export interface ArticlesApiResponse {
  total: number;
  pageSize: number;
  pageStart: number;
  articleList: ArticleListItem[];
  filters: ArticlesFilters;
}

export interface ArticlesQueryArgs {
  /** CMS-provided endpoint the listing posts requests to. */
  endpoint: string;
  /**
   * Keeps the RTK Query cache page-specific - by convention this is the
   * route pathname the ArticleListing instance is rendered on, since the
   * requirements confirm only a single listing instance exists per page.
   */
  instanceKey: string;
  constantFilters: Record<string, unknown>;
  appliedFilters: Record<string, unknown>;
  offset: number;
  limit: number;
  /**
   * Request-shaping only (see buildArticlesQueryArgs in articleHelpers.ts) -
   * whether *this* request should ask for one extra article to cover the
   * featured/hero slot. Deliberately excluded from
   * normalizeArticlesQueryArgs: it's true only for the very first fetch of
   * a session (offset 0) and false for every Load More page after it, so
   * including it in the cache key would fragment the merged-pages cache
   * entry that `offset` is already excluded to keep whole.
   */
  withFeaturedSlot?: boolean;
  context?: unknown;
}

const sortEntries = (obj: Record<string, unknown> = {}) =>
  Object.keys(obj)
    .filter((key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== '')
    .sort()
    .reduce((acc: Record<string, unknown>, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});

const normalizeCategoryFilter = (category: unknown) =>
  (Array.isArray(category) ? category : [])
    .map((item: { id?: string; key?: string }) => item?.id || item?.key)
    .filter(Boolean)
    .sort();

/**
 * Deterministic normalization of query args used to build the RTK Query
 * cache key. Input key order must never affect the resulting key, and
 * empty/default filter values are dropped so equivalent filter states
 * collapse onto the same cache entry.
 *
 * `offset` is deliberately excluded - see `serializeQueryArgs` below.
 */
export const normalizeArticlesQueryArgs = (args: ArticlesQueryArgs) => {
  const { endpoint, instanceKey, constantFilters, appliedFilters, limit } = args;
  const normalizedApplied = sortEntries({
    ...appliedFilters,
    category: normalizeCategoryFilter(appliedFilters?.category),
  });

  return {
    endpoint,
    instanceKey,
    limit,
    constantFilters: sortEntries(constantFilters),
    appliedFilters: normalizedApplied,
  };
};

const buildRequestBody = (args: ArticlesQueryArgs) => {
  const { constantFilters, appliedFilters, offset, limit, context, withFeaturedSlot } = args;

  // Mirrors the legacy getArticles thunk's `pageSize + 1` "reset"
  // compensation: when the featured slot will be shown, one extra article
  // is requested since it's sliced off the front of the results for the
  // hero card, so the grid still ends up with `limit` cards.
  const pageSize = withFeaturedSlot ? limit + 1 : limit;

  return objectForReq({
    ...constantFilters,
    ...appliedFilters,
    pageSize,
    pageStart: offset,
    context,
  });
};

// Stable identity for an article, used to dedupe pages on merge. `id` is
// preferred; `url` is a reliable fallback (every card links somewhere), and
// the JSON stringify is a last resort so two genuinely-distinct-but-id-less
// articles still don't collapse into one.
const articleKey = (article: ArticleListItem): string =>
  String(article?.id ?? article?.url ?? JSON.stringify(article));

interface ArticlesBaseQueryArgs {
  endpoint: string;
  body: Record<string, unknown>;
}

interface ArticlesBaseQueryError {
  status?: number;
  message: string;
}

const articlesBaseQuery: BaseQueryFn<
  ArticlesBaseQueryArgs,
  ArticlesApiResponse,
  ArticlesBaseQueryError
> = async ({ endpoint, body }) => {
  try {
    const response = await apiClient.post(endpoint, body, {
      headers: authHeader('U0pQOlBhcnRuZXI=', 'application/json'),
    });

    if (!response?.data) {
      return { error: { message: 'Empty response received from articles endpoint' } };
    }

    return { data: response.data as ArticlesApiResponse };
  } catch (error) {
    logger.error(error);

    return {
      error: {
        status: (error as { response?: { status?: number } })?.response?.status,
        message: error instanceof Error ? error.message : 'Unknown error fetching articles',
      },
    };
  }
};

export const articlesApi = createApi({
  reducerPath: 'articlesApi',
  baseQuery: articlesBaseQuery,
  endpoints: (builder) => ({
    getArticlesPage: builder.query<ArticlesApiResponse, ArticlesQueryArgs>({
      query: (args) => ({
        endpoint: args.endpoint,
        body: buildRequestBody(args),
      }),
      // Don't trust the backend's own `pageStart` echo for this - pin it to
      // the offset we actually requested. This is the value ArticleResults
      // relies on (as the session's starting position) to compute the next
      // Load More offset, so it needs to be something we control
      // deterministically rather than whatever pagination semantics the
      // backend happens to apply for a given filter combination.
      transformResponse: (response: ArticlesApiResponse, _meta, arg: ArticlesQueryArgs) => ({
        ...response,
        pageStart: arg.offset,
      }),
      // Cache key intentionally excludes `offset`: every "page" fetched for
      // the same endpoint/filters/instance is merged into a single cache
      // entry below, so "Load More" accumulates results instead of
      // replacing them, while a fresh navigation straight to a deep
      // ?offset=N URL still only requests (and shows) that slice.
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const normalized = normalizeArticlesQueryArgs(queryArgs);
        return `${endpointName}(${JSON.stringify(normalized)})`;
      },
      merge: (currentCache, newData) => {
        currentCache.total = newData.total;
        currentCache.pageSize = newData.pageSize;
        // Deliberately NOT overwritten with newData.pageStart: this must
        // stay the offset of the *first* fetch in this session so
        // ArticleResults can compute the true "items loaded so far"
        // position on every subsequent Load More, even when the session
        // started from a deep-linked, non-zero offset.
        currentCache.filters = newData.filters;
        // Dedupe on merge: circular ("Load More" wrapping past the end back
        // to offset 0) pagination can request a page that overlaps one
        // already loaded when offset/limit aren't aligned. Appending blindly
        // would show duplicates and inflate articleList.length, which
        // ArticleResults compares against `total` to decide when to stop.
        const seen = new Set(currentCache.articleList.map(articleKey));
        currentCache.articleList.push(
          ...newData.articleList.filter((article) => !seen.has(articleKey(article))),
        );
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        return currentArg?.offset !== previousArg?.offset;
      },
    }),
  }),
});

export const { useGetArticlesPageQuery, useLazyGetArticlesPageQuery } = articlesApi;
